/**
 * CombatEvents - Combat-Related Socket Event Handlers
 *
 * Handles events related to combat, damage, and elimination:
 * - damageTaken: Local player took damage
 * - damageDealt: Local player dealt damage
 * - playerEliminated: A player was eliminated
 * - playerDied: Local player died
 * - gameEnd: Game has ended
 */

import { weaponRegistry } from '../../../shared/WeaponRegistry.js';
import { resolveSound } from '../../../shared/resolveSound.js';
import { SPATIAL_AUDIO_CONFIG } from '../../audio/spatial-audio-config.js';
import { ClientLogger } from '../../logger/ClientLogger.js';
import { PixiCamera } from '../../rendering/pixi/PixiCamera.js';

const combatEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {}, debug: () => {} };
})();
const combatEventsLogger = ClientLogger.getLogger('combat') || combatEventsLoggerFallback;

const CombatEvents = {
  /**
   * Register all combat-related event handlers
   * @param {Socket} socket - Socket.io socket instance
   * @param {GameState} gameState - Game state container
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.showEliminationNotification - Show kill notification
   * @param {Function} callbacks.addEliminationToFeed - Add to kill feed
   * @param {Function} callbacks.showGameEndScreen - Show victory/defeat screen
   * @param {Function} callbacks.createFloatingDamageNumber - Create damage number
   * @param {Function} callbacks.syncIsInGame - Sync isInGame local accessor
   */
  register(socket, gameState, callbacks) {
    const {
      showEliminationNotification,
      addEliminationToFeed,
      showGameEndScreen,
      createFloatingDamageNumber,
      syncIsInGame,
      showDeathNotification,
      playDeathSound,
      playEliminationSound,
      spatialAudio = null
    } = callbacks;
    const getAllowRespawn = () => gameState.gameModeConfig?.allowRespawn ?? false;

    // Local player took damage
    socket.on('damageTaken', (data) => {
      if (gameState.localId && data.damage) {
        const player = gameState.players.get(gameState.localId);
        if (player && createFloatingDamageNumber) {
          createFloatingDamageNumber(gameState.localId, data.damage, player.x, player.y, {
            shieldDamage: data.shieldDamage || 0,
            healthDamage: data.healthDamage || data.damage
          });
        }
      }
    });

    // Player hit sound - broadcast to all players in room
    socket.on('playerHitSound', (data) => {
      if (!data || !data.playerId) return;

      const resolved = resolveSound(gameState.gameConfig?.player?.sounds?.damage);
      const soundPath = resolved?.path || 'assets/sound/player/player_taking_damage.mp3';
      const volume = resolved ? resolved.volume : 0.5;

      // Use spatial audio if available and this is not the local player
      if (spatialAudio && data.playerId !== gameState.localId && data.x !== undefined && data.y !== undefined) {
        // Spatial audio for other players
        spatialAudio.playAt(soundPath, data.x, data.y, {
          config: SPATIAL_AUDIO_CONFIG.damage,
          volume: volume
        });
      } else if (spatialAudio && data.playerId === gameState.localId) {
        // Local sound (no spatialization) for own damage
        spatialAudio.playLocal(soundPath, { volume: volume });
      } else {
        // Fallback to HTML5 Audio if spatial audio not available
        const damageSound = new Audio(soundPath);
        damageSound.volume = volume;
        damageSound.currentTime = 0;
        damageSound.play().catch(err => combatEventsLogger.warn('Failed to play damage sound:', err));
      }
    });

    // Bullet hit a wall or other non-player object - play impact sound
    // Testing mode only (the server sends nothing otherwise): where the server had the
    // target when your shot hit. Drawn beside where your screen had it (spec D9).
    socket.on('hitregDebug', (data) => {
      if (!data || !Number.isFinite(data.targetX) || !Number.isFinite(data.targetY)) return;
      gameState.hitregDebugActive = true;
      gameState.hitregMarks.push({ x: data.targetX, y: data.targetY, r: data.radius, rewindMs: data.rewindMs, startTime: performance.now() });
    });

    socket.on('projectileHit', (data) => {
      if (!data || data.x === undefined || data.y === undefined) return;

      const resolved = resolveSound(gameState.gameConfig?.general?.sounds?.bullet_wall_hit);
      const soundPath = resolved?.path || 'assets/sound/general/bullet_wall_hit.mp3';
      const volume = resolved ? resolved.volume : 0.5;

      if (spatialAudio) {
        spatialAudio.playAt(soundPath, data.x, data.y, {
          config: SPATIAL_AUDIO_CONFIG.damage,
          volume
        });
      } else {
        const hitSound = new Audio(soundPath);
        hitSound.volume = volume;
        hitSound.play().catch(err => combatEventsLogger.warn('Failed to play bullet wall hit sound:', err));
      }
    });

    // Local player dealt damage to someone
    socket.on('damageDealt', (data) => {
      // Update local player's damage dealt stat
      const localPlayer = gameState.players.get(gameState.localId);
      if (localPlayer && localPlayer.stats && data.damage) {
        localPlayer.stats.damageDealt = (localPlayer.stats.damageDealt || 0) + data.damage;
      }

      if (data.targetId && data.damage) {
        const targetPlayer = gameState.players.get(data.targetId);
        if (targetPlayer && createFloatingDamageNumber) {
          createFloatingDamageNumber(data.targetId, data.damage, targetPlayer.x, targetPlayer.y, {
            shieldDamage: data.shieldDamage || 0,
            healthDamage: data.healthDamage || data.damage
          });
        }

        // Create off-screen hit indicator if target is off-screen
        // Use position from event data (works even when target is outside viewport culling range)
        const targetX = data.targetX ?? targetPlayer?.x;
        const targetY = data.targetY ?? targetPlayer?.y;

        if (targetX !== undefined && targetY !== undefined && typeof PixiCamera !== 'undefined' && PixiCamera.screenWidth) {
          const screenPos = PixiCamera.worldToScreen(targetX, targetY);
          const screenX = screenPos.x;
          const screenY = screenPos.y;
          // Check if target is outside the visible screen (with small inner margin so near-edge targets don't flicker)
          const innerMargin = 50;
          const isOffScreen = screenX < innerMargin || screenX > PixiCamera.screenWidth - innerMargin ||
                              screenY < innerMargin || screenY > PixiCamera.screenHeight - innerMargin;

          if (isOffScreen) {
            // Initialize hit indicators array if needed
            if (!gameState.offScreenHitIndicators) {
              gameState.offScreenHitIndicators = [];
            }

            gameState.offScreenHitIndicators.push({
              targetX: targetX,
              targetY: targetY,
              damage: data.damage,
              shieldDamage: data.shieldDamage || 0,
              healthDamage: data.healthDamage || data.damage,
              timestamp: Date.now(),
              duration: 2000 // Show for 2 seconds
            });
          }
        }
      }
    });

    // A player was eliminated
    socket.on('playerEliminated', (data) => {
      const killerPlayer = gameState.players.get(data.eliminatingPlayer);
      let killerKills = typeof data.eliminations === 'number' ? data.eliminations : 0;
      if (killerPlayer && killerPlayer.stats) {
        if (typeof data.eliminations === 'number') {
          killerPlayer.stats.kills = data.eliminations;
        } else {
          killerPlayer.stats.kills = (killerPlayer.stats.kills || 0) + 1;
        }
        killerKills = killerPlayer.stats.kills;
      }
      if (typeof gameState.updateScoreboardPlayer === 'function') {
        gameState.updateScoreboardPlayer(data.eliminatingPlayer, {
          name: data.eliminatingPlayerName,
          kills: killerKills,
          deaths: killerPlayer?.stats?.deaths
        });
      }

      if (data.eliminatingPlayer === gameState.localId) {
        // Show center screen notification for local player's kills
        if (showEliminationNotification) {
          showEliminationNotification(data.eliminatedPlayer, data.eliminatedPlayerName);
        }
        if (playEliminationSound) {
          playEliminationSound();
        }
      }

      gameState.recordDeath?.(data.eliminatedPlayer, performance.now());

      // Update eliminated player's death count (if still in players map)
      const eliminatedPlayer = gameState.players.get(data.eliminatedPlayer);
      if (eliminatedPlayer) {
        if (eliminatedPlayer.stats) {
          eliminatedPlayer.stats.deaths = (eliminatedPlayer.stats.deaths || 0) + 1;
        }
        eliminatedPlayer.isDead = true;
        eliminatedPlayer.deathTime = performance.now(); // Track death time for fade animation
        if (eliminatedPlayer.health) {
          eliminatedPlayer.health.currentHealth = 0;
        }
      }
      if (typeof gameState.updateScoreboardPlayer === 'function') {
        const victimDeaths = eliminatedPlayer?.stats?.deaths ?? 0;
        const victimKills = eliminatedPlayer?.stats?.kills ?? 0;
        gameState.updateScoreboardPlayer(data.eliminatedPlayer, {
          name: data.eliminatedPlayerName,
          kills: victimKills,
          deaths: victimDeaths
        });
      }

      // If the eliminated player is us, handle local-only notifications/sounds
      if (data.eliminatedPlayer === gameState.localId && eliminatedPlayer) {
        if (playDeathSound) {
          playDeathSound();
        }
        if (getAllowRespawn() && showDeathNotification) {
          showDeathNotification(data.eliminatingPlayerName || data.eliminatingPlayer);
        }
      }

      // Add elimination to feed with player names
      if (addEliminationToFeed) {
        addEliminationToFeed(
          data.eliminatingPlayer,
          data.eliminatedPlayer,
          data.eliminatingPlayerName,
          data.eliminatedPlayerName
        );
      }

    });

    // Respawn scheduled - start countdown
    socket.on('respawnScheduled', (data) => {
      const localPlayer = gameState.players.get(gameState.localId);
      if (localPlayer && data) {
        localPlayer.respawnScheduledAt = performance.now();
        localPlayer.respawnDelay = data.respawnDelay;
        combatEventsLogger.info(`Respawn scheduled in ${(data.respawnDelay / 1000).toFixed(1)}s`);
      }
    });

    // Local player died
    socket.on('playerDied', (data) => {
      const localPlayer = gameState.players.get(gameState.localId);
      if (localPlayer) {
        localPlayer.isDead = true;
        localPlayer.deathTime = performance.now(); // Track death time for fade animation
        if (localPlayer.health) {
          localPlayer.health.currentHealth = 0;
        }
        // Track death in local stats
        if (localPlayer.stats) {
          localPlayer.stats.deaths = (localPlayer.stats.deaths || 0) + 1;
        }
      }

      // Only show defeated screen if respawn is not allowed
      const allowRespawn = getAllowRespawn();
      if (!allowRespawn) {
        gameState.isInGame = false;
        if (syncIsInGame) syncIsInGame();

        const gameEndData = {
          victory: false,
          winnerName: null,
          killedBy: data?.killedBy || null,
          killedByName: data?.killedByName || 'Unknown',
          eliminations: data?.eliminations || 0,
          damageDealt: data?.damageDealt || 0,
          damageBlocked: data?.damageBlocked || 0,
          abilities: data?.abilities || [],
          // The match is still going for everyone else - GameEndScreen offers Spectate.
          phase: 'eliminated',
          rankedPending: Boolean(gameState.gameModeConfig?.metadata?.ranked)
        };

        if (showGameEndScreen) {
          showGameEndScreen(gameEndData);
        }
      }
    });

    // While spectating, the server picks who we follow (real players before
    // bots) and re-picks when that player falls.
    socket.on('spectateTarget', (data) => {
      gameState.spectateTargetId = data?.targetId || null;
    });

    // Game ended - victory or defeat
    socket.on('gameEnd', (data) => {
      gameState.isInGame = false;
      if (syncIsInGame) syncIsInGame();

      // Extract player-specific data from the broadcast format
      const myStats = data.stats?.[gameState.localId] || {};
      const gameEndData = {
        victory: myStats.victory === true,
        winnerName: data.winnerName,
        winningTeam: data.winningTeam ?? null,
        eliminations: myStats.eliminations || 0,
        damageDealt: myStats.damageDealt || 0,
        // This match's per-weapon breakdown, already in the payload (spec: no round trip)
        weapons: myStats.weapons || [],
        // What their defensive abilities blocked this match, also already in the payload
        damageBlocked: myStats.damageBlocked || 0,
        abilities: myStats.abilities || [],
        // The match is truly over - no one left to spectate.
        phase: 'matchEnd',
        rankedPending: Boolean(gameState.gameModeConfig?.metadata?.ranked)
      };

      // Stop any ongoing spatial or ambient combat sounds
      if (spatialAudio && typeof spatialAudio.stopAll === 'function') {
        spatialAudio.stopAll();
      }

      // Play victory sound if local player won
      if (gameEndData.victory) {
        const victorySound = gameState.gameConfig?.general?.sounds?.round_winner;
        const resolved = resolveSound(victorySound);

        if (resolved) {
          const audio = new Audio(resolved.path);
          audio.volume = resolved.volume;
          audio.loop = victorySound.loop ?? false;
          if (victorySound.playbackRate) {
            audio.playbackRate = victorySound.playbackRate;
          }
          audio.play().catch(err => combatEventsLogger.warn('Failed to play victory sound:', err));
        }
      }

      if (showGameEndScreen) {
        showGameEndScreen(gameEndData);
      }
    });

    // Weapon equipped successfully
    socket.on('weaponEquipSuccess', (data) => {
      if (!data) return;
      const { weaponId, weaponName } = data;
      combatEventsLogger.info(`✅ Weapon equipped: ${weaponName} (${weaponId})`);

      // Update local player's equipped weapon
      const localPlayer = gameState.players.get(gameState.localId);
      if (localPlayer && typeof weaponRegistry !== 'undefined') {
        const weapon = weaponRegistry.get(weaponId);
        if (weapon) {
          localPlayer.equipWeapon(weapon);
        }
      }
    });

    // Weapon equip failed
    socket.on('weaponEquipFailed', (data) => {
      const reason = data?.reason || 'Unknown';
      combatEventsLogger.warn(`❌ Weapon equip failed: ${reason}`);
    });
  }
};

// Export for use in other modules
// For now, CombatEvents is a global object loaded via script tag

export { combatEventsLoggerFallback, combatEventsLogger, CombatEvents };
